// Утилита для безопасного извлечения унифицированного APIErrorBody из axios-ошибок.
// Защита от случаев, когда сервер вернул что-то нестандартное (HTML 502 от прокси,
// network error без response и т.п.) — никогда не падаем при чтении err.response.data.
import { AxiosError, isAxiosError } from "axios";
import type { APIErrorBody, APIErrorCode } from "./types";
import { ERROR_CODE_DEFAULT_MESSAGES } from "./types";

export interface NormalizedError {
  /** HTTP статус (0 — network error). */
  status: number;
  /** Машинно-читаемый код по контракту PRD §11.7. */
  code: APIErrorCode;
  /** Сообщение для toast'а (русский). */
  message: string;
  /** Дополнительные детали из бэкенда (если есть). */
  details?: Record<string, unknown>;
  /** Только для 423/429: пауза до следующей попытки. */
  retry_after_seconds?: number;
}

function pickCode(status: number): APIErrorCode {
  if (status === 401) return "unauthenticated";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  if (status === 422) return "business_rule_violation";
  if (status === 423) return "account_locked";
  if (status === 429) return "rate_limited";
  if (status === 502) return "provider_unavailable";
  if (status === 504) return "provider_timeout";
  if (status >= 500) return "internal_error";
  if (status === 400) return "validation_error";
  return "internal_error";
}

/** Преобразует любую ошибку (axios + не-axios) в NormalizedError. */
export function normalizeError(err: unknown): NormalizedError {
  if (isAxiosError(err)) {
    const ax = err as AxiosError<Partial<APIErrorBody>>;
    const status = ax.response?.status ?? 0;
    const body = ax.response?.data;

    // Бэкенд вернул контракт §11.7.
    if (body && typeof body === "object" && "error" in body && body.error) {
      const e = body.error;
      const details = e.details ?? undefined;
      const retry =
        details && typeof details["retry_after_seconds"] === "number"
          ? (details["retry_after_seconds"] as number)
          : undefined;
      return {
        status,
        code: e.code ?? pickCode(status),
        message: e.message || ERROR_CODE_DEFAULT_MESSAGES[e.code ?? "internal_error"],
        details,
        retry_after_seconds: retry,
      };
    }

    // Network error (нет response).
    if (!ax.response) {
      return {
        status: 0,
        code: "internal_error",
        message: "Нет соединения с сервером. Проверьте сеть и попробуйте ещё раз.",
      };
    }

    // HTTP ошибка без контракта — даём дефолтное сообщение по статусу.
    const code = pickCode(status);
    return {
      status,
      code,
      message: ERROR_CODE_DEFAULT_MESSAGES[code],
    };
  }

  if (err instanceof Error) {
    return { status: 0, code: "internal_error", message: err.message };
  }
  return { status: 0, code: "internal_error", message: "Неизвестная ошибка" };
}
